import * as lzString from 'lz-string';
import { type MultiPolygonG2D } from '@ev-tech/search-modules-api';

/** Minimum vertices in a ring (closed polygon). */
const MIN_RING_LENGTH = 4;
/** [lng, lat] (or more dimensions, if present). */
const MIN_POINT_LENGTH = 2;

const isNumeric = (coord: unknown): coord is number => typeof coord === 'number';

const isLngLatPoint = (point: unknown): boolean => {
  if (!Array.isArray(point) || point.length < MIN_POINT_LENGTH) {
    return false;
  }
  return point.every(isNumeric);
};

const isClosedRing = (ring: unknown): boolean => {
  if (!Array.isArray(ring) || ring.length < MIN_RING_LENGTH) {
    return false;
  }
  return ring.every(isLngLatPoint);
};

const isNonEmptyPolygon = (polygon: unknown): boolean => {
  if (!Array.isArray(polygon) || polygon.length === 0) {
    return false;
  }
  return polygon.every(isClosedRing);
};

const isValidMultiPolygonCoordinates = (value: unknown): value is number[][][][] => {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every(isNonEmptyPolygon);
};

export function parseMultiPolygonFromUrlString(encoded: string | undefined): MultiPolygonG2D | undefined {
  if (!encoded) {
    return;
  }

  const decoded = lzString.decompressFromEncodedURIComponent(encoded);

  if (!decoded) {
    return;
  }

  try {
    const parsed: unknown = JSON.parse(decoded);
    if (!isValidMultiPolygonCoordinates(parsed)) {
      return;
    }
    return { type: 'MultiPolygon', coordinates: parsed };
  } catch {
    return;
  }
}
